// ABOUTME: Catch-all 404 route — the in-cabinet status screen, plus Sentry logging of the bad URL.
// ABOUTME: Was a silent Navigate to "/", which hid broken links from the user and swallowed the back stack.

import { useEffect, type FC } from "react"
import { useLocation, useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { CStatusScreen } from "@shared/ui"
import { CALENDAR_PAGE } from "@shared/constants/navigation"
import { SUPPORT_URL } from "@shared/constants/navigation"

export const NotFoundPage: FC = function () {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    // Title uses pathname only — Sentry groups by message string, so a query
    // that varies per visitor (auth tokens, share IDs, …) would explode into
    // one issue per request. The full URL still rides along in `extra` for
    // diagnostics; Sentry's PII scrubber cleans known-sensitive keys from
    // structured data on ingest.
    Sentry.captureMessage(`404: ${location.pathname}`, {
      level: "warning",
      tags: { kind: "route_not_found" },
      extra: {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        referrer: typeof document !== "undefined" ? document.referrer : "",
      },
    })
  }, [location.hash, location.pathname, location.search])

  // Straight to the calendar when there is nothing to go back to — a 404 opened from a
  // mail link or a bookmark is the first entry in this tab's history, so `-1` would
  // leave the app (or do nothing at all).
  const goBack = () => void (window.history.length > 1 ? navigate(-1) : navigate(CALENDAR_PAGE, { replace: true }))

  return (
    <CStatusScreen
      title={t("notFound.title")}
      code="404"
      description={t("notFound.description")}
      // `replace` keeps the dead URL out of the back stack: going back from the calendar
      // must not land on the 404 again.
      primaryAction={{
        label: t("notFound.calendar"),
        onClick: () => navigate(CALENDAR_PAGE, { replace: true }),
      }}
      secondaryAction={{ label: t("common.back"), onClick: goBack }}
      footer={
        <>
          <span>{t("notFound.supportPrompt")}</span>
          <a
            href={SUPPORT_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="font-medium text-primary hover:underline"
          >
            {t("notFound.supportLink")}
          </a>
        </>
      }
    />
  )
}
